/**
 * Regla de sampling de los modelos Claude 4+ en Bedrock.
 *
 * Vive aparte del servicio a propósito: acá no se importa Angular ni `environment`, así que es
 * lógica pura y testeable sin levantar el grafo de módulos de la app.
 */

/** Solo lo que a esta regla le importa; el resto de la config pasa intacto. */
export interface SamplingFields {
  temperature?: number;
  topP?: number;
}

export interface SamplingDropInfo {
  /** Campo descartado y su valor, para poder loguearlo del lado del servicio. */
  campo: 'topP';
  valor: number;
}

/**
 * Deja pasar una sola de las dos formas de controlar el sampling.
 *
 * Los modelos Claude 4+ rechazan el request COMPLETO si vienen `temperature` y `topP` juntos:
 * "`temperature` and `top_p` cannot both be specified for this model". Los Claude 3 los aceptaban,
 * así que esto aparece recién al actualizar el modelo — y aparece en runtime, contra el modelo real,
 * que es el peor momento para enterarse.
 *
 * Se conserva `temperature` porque es lo que usan todos los llamadores actuales.
 *
 * Devuelve la MISMA referencia si no hubo nada que cambiar, así el llamador puede evitar el clon.
 */
export function normalizeSamplingConfiguration<T extends object>(
  configuration: T | undefined,
  onDrop?: (info: SamplingDropInfo) => void
): T | undefined {
  // Tolera null en runtime aunque el tipo no lo contemple: viene de payloads armados a mano.
  if (!configuration) return configuration;

  // `T extends object` en vez de `T extends SamplingFields` para que una config sin campos de
  // sampling (solo maxTokens, por ejemplo) no choque con el weak type detection de TS.
  const sampling = configuration as T & SamplingFields;

  // Comparar contra undefined y no por falsy: 0 es un valor válido de temperature y de topP.
  if (sampling.temperature === undefined || sampling.topP === undefined) return configuration;

  if (onDrop) onDrop({ campo: 'topP', valor: sampling.topP });

  const { topP, ...rest } = sampling;
  return rest as T;
}
